
import React, { useState, useMemo, useEffect } from 'react';
import XIcon from './icons/XIcon';
import UsersIcon from './icons/UsersIcon';
import UserIcon from './icons/UserIcon';
import CogIcon from './icons/CogIcon';
import LogoutIcon from './icons/LogoutIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import MeatIcon from './icons/MeatIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import SparklesIcon from './icons/SparklesIcon';
import ChefHatIcon from './icons/ChefHatIcon';
import CalendarIcon from './icons/CalendarIcon';
import CalendarDaysIcon from './icons/CalendarDaysIcon';
import ListBulletIcon from './icons/ListBulletIcon';
import AvatarImage from './AvatarImage';
import SearchField from './SearchField';
import GlobeIcon from './icons/GlobeIcon';
import FastForwardIcon from './icons/FastForwardIcon';
import MessageCircleIcon from './icons/MessageCircleIcon';
import ClipboardIcon from './icons/ClipboardIcon';
import BellIcon from './icons/BellIcon';
import type { User, Household, Recipe } from '../types';
import { getTranslation } from '../utils/translations';
import { formatAdminBadgeCount } from '../utils/adminNotifications';
import { getUserProfileDisplay } from '../utils/userDisplay';
import { isGuidedUser } from '../utils/accountMode';
import { getConsultantSwitchableHouseholds, isGuidedClientHousehold, canAccessGuidedClientsAdmin } from '../utils/guidedClientHousehold';
import runningVersionData from '../version.json';

interface SideMenuProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    authUser?: User | null;
    isImpersonating?: boolean;
    onExitImpersonation?: () => void;
    activeHouseholdId?: string;
    households: Household[];
    recipes: Recipe[];
    onSwitchHousehold: (householdId: string) => void;
    onLogout: () => void;
    onUserManagementClick: () => void;
    onHouseholdManagementClick: () => void;
    onRecipeSelect: (recipe: Recipe) => void;
    onPersonalDetailsClick: () => void;
    onSettingsClick: () => void;
    onSubscriptionManagementClick?: () => void;
    onTosClick: () => void;
    onContactClick: () => void;
    onHowToClick: () => void;
    onSuggestionBoxClick: () => void;
    onConsultationBoxClick?: () => void;
    onSuggestionManagementClick: () => void;
    onChefManagementClick?: () => void;
    onGuidedClientsClick?: () => void;
    onPhraseOfDayAdminClick?: () => void;
    onTabChange: (tab: 'plan' | 'recipes' | 'lists' | 'monthly') => void;
    pendingSubscriptionCount?: number;
    newSuggestionsCount?: number;
    unreadNotificationCount?: number;
    onNotificationsClick?: () => void;
}

const SideMenu: React.FC<SideMenuProps> = ({
    isOpen,
    onClose,
    user,
    authUser,
    isImpersonating = false,
    onExitImpersonation,
    activeHouseholdId,
    households,
    recipes,
    onSwitchHousehold,
    onLogout,
    onUserManagementClick,
    onHouseholdManagementClick,
    onRecipeSelect,
    onPersonalDetailsClick,
    onSettingsClick,
    onSubscriptionManagementClick,
    onTosClick,
    onContactClick,
    onHowToClick,
    onSuggestionBoxClick,
    onConsultationBoxClick,
    onSuggestionManagementClick,
    onChefManagementClick,
    onGuidedClientsClick,
    onPhraseOfDayAdminClick,
    onTabChange,
    pendingSubscriptionCount = 0,
    newSuggestionsCount = 0,
    unreadNotificationCount = 0,
    onNotificationsClick,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    const [displayVersion, setDisplayVersion] = useState(runningVersionData.version);
    const t = (key: any) => getTranslation(user.language, key);

    useEffect(() => {
        let cancelled = false;

        const loadVersion = async () => {
            try {
                const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
                if (!response.ok) {
                    return;
                }

                const data = await response.json() as { version?: string };
                if (!cancelled && data.version) {
                    setDisplayVersion(data.version);
                }
            } catch {
                // Keep bundled fallback version.
            }
        };

        void loadVersion();

        return () => {
            cancelled = true;
        };
    }, []);

    const adminUser = authUser ?? user;
    const viewUser = isImpersonating ? user : adminUser;
    const canManageUsers = !isImpersonating && (adminUser.role === 'admin' || adminUser.role === 'superuser');
    const isSuperuser = !isImpersonating && adminUser.role === 'superuser';
    const canManageGuidedClients = canAccessGuidedClientsAdmin(viewUser);
    const guided = isGuidedUser(user);

    const pendingSubsBadge = isSuperuser ? formatAdminBadgeCount(pendingSubscriptionCount) : null;
    const newSuggestionsBadge = isSuperuser ? formatAdminBadgeCount(newSuggestionsCount) : null;
    const adminAttentionCount = (pendingSubscriptionCount > 0 ? 1 : 0) + (newSuggestionsCount > 0 ? 1 : 0);
    const notificationsBadge = formatAdminBadgeCount(unreadNotificationCount);

    const switchableHouseholds = useMemo(
        () => getConsultantSwitchableHouseholds(viewUser, households),
        [viewUser, households],
    );

    const profileDisplay = useMemo(() => getUserProfileDisplay(user), [user]);

    const toggleSection = (section: string) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    const searchResults = useMemo(() => {
        if (guided || !searchQuery.trim()) return [];
        const lowerQuery = searchQuery.toLowerCase();
        return recipes.filter(recipe => {
            if (recipe.isDeleted) return false;
            const matchName = recipe.name.toLowerCase().includes(lowerQuery);
            const matchIng = recipe.ingredients.some(ing => ing.name.toLowerCase().includes(lowerQuery));
            const matchTransName = recipe.englishTranslation?.name?.toLowerCase().includes(lowerQuery);
            const matchTransIng = recipe.englishTranslation?.ingredients?.some(ing => ing.name.toLowerCase().includes(lowerQuery));
            return matchName || matchIng || matchTransName || matchTransIng;
        });
    }, [searchQuery, recipes, guided]);

    const handleSearchResultClick = (recipe: Recipe) => {
        onRecipeSelect(recipe);
        onClose();
        setSearchQuery('');
    };

    const handleAction = (action: () => void) => {
        action();
        onClose();
    };

    const drawerPositionClass = "start-0"; 
    const translateClass = isOpen 
        ? 'translate-x-0' 
        : 'ltr:-translate-x-full rtl:translate-x-full';

    return (
        <>
            <div 
                className={`fixed inset-0 bg-black/50 z-[140] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />

            <div className={`fixed inset-y-0 ${drawerPositionClass} w-[85%] max-w-sm bg-white dark:bg-slate-900 z-[150] shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${translateClass}`}>
                
                <div className="flex justify-between items-center px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                    <h1 className="font-nunito font-black text-2xl text-brand-sage tracking-tighter select-none">
                        mealPai
                    </h1>
                    <button onClick={onClose} className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => handleAction(onPersonalDetailsClick)}
                    className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-start w-full"
                >
                    <AvatarImage
                        src={user.avatarUrl}
                        alt={profileDisplay.avatarLabel}
                        className="w-11 h-11 rounded-full object-cover border-2 border-white dark:border-slate-900 shadow-sm bg-brand-sage/15 flex-shrink-0"
                        fallback={
                            <div className="w-11 h-11 rounded-full bg-brand-sage/15 text-brand-sage flex items-center justify-center font-black text-lg flex-shrink-0 border-2 border-white dark:border-slate-900">
                                {(profileDisplay.avatarLabel[0] || '?').toUpperCase()}
                            </div>
                        }
                    />
                    <div className="min-w-0 flex-1">
                        {profileDisplay.primaryLabel && (
                            <p className={`font-black text-slate-900 dark:text-slate-100 truncate leading-tight ${profileDisplay.primaryIsEmail ? 'dir-ltr text-start' : ''}`}>
                                {profileDisplay.primaryLabel}
                            </p>
                        )}
                        {profileDisplay.showFullName && (
                            <p className="text-sm text-slate-600 dark:text-slate-300 truncate mt-0.5">
                                {profileDisplay.fullName}
                            </p>
                        )}
                        {profileDisplay.showEmail && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5 dir-ltr text-start">
                                {profileDisplay.email}
                            </p>
                        )}
                    </div>
                </button>

                <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <SearchField
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={t('search')}
                        clearLabel={t('clearSearch')}
                        iconClassName="w-5 h-5"
                        inputClassName="rounded-none bg-white dark:bg-slate-900 focus:ring-0 focus:border-slate-400 dark:focus:border-slate-500"
                    />
                </div>

                <div className="flex-grow overflow-y-auto">
                    {searchQuery.trim().length > 0 ? (
                        <div className="bg-white dark:bg-slate-900">
                             <div className="p-3 text-xs font-semibold text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800">{t('searchResults')}</div>
                             {searchResults.length > 0 ? (
                                 <ul>
                                     {searchResults.map(recipe => (
                                         <li key={recipe.id}>
                                             <button 
                                                onClick={() => handleSearchResultClick(recipe)}
                                                className="w-full text-start p-4 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between group transition-colors"
                                             >
                                                 <span className="font-medium text-slate-700 dark:text-slate-300 group-hover:text-sky-600 dark:group-hover:text-sky-400">{recipe.name}</span>
                                                 {(recipe.dietTypes?.isKeto ?? recipe.isKeto) && <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-1.5 py-0.5 rounded">Keto</span>}
                                             </button>
                                         </li>
                                     ))}
                                 </ul>
                             ) : (
                                 <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                                     {t('noResults')}
                                 </div>
                             )}
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {/* Fallback Navigation Links */}
                            <div>
                                <button 
                                    onClick={() => toggleSection('nav')}
                                    className="w-full flex justify-between items-center p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <span className="font-medium text-slate-700 dark:text-slate-300">{t('navigation')}</span>
                                    {expandedSection === 'nav' ? <ChevronUpIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
                                </button>
                                {expandedSection === 'nav' && (
                                    <div className="bg-slate-50 dark:bg-slate-800/30 py-2">
                                        <button 
                                            onClick={() => handleAction(() => onTabChange('recipes'))}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <ChefHatIcon className="w-4 h-4" /> {t('recipes')}
                                        </button>
                                        <button 
                                            onClick={() => handleAction(() => onTabChange('plan'))}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <CalendarIcon className="w-4 h-4" /> {t('weeklyPlan')}
                                        </button>
                                        <button 
                                            onClick={() => handleAction(() => onTabChange('monthly'))}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <CalendarDaysIcon className="w-4 h-4" /> {t('monthlyPlan')}
                                        </button>
                                        <button 
                                            onClick={() => handleAction(() => onTabChange('lists'))}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <ListBulletIcon className="w-4 h-4" /> {t('shoppingCarts')}
                                        </button>
                                        <div
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 flex items-center justify-between transition-colors"
                                        >
                                            <span className="flex items-center gap-3">
                                                <GlobeIcon className="w-4 h-4" /> {t('about')}
                                            </span>
                                            <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">
                                                v{displayVersion}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleAction(onHowToClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <FastForwardIcon className="w-4 h-4" /> {t('getStarted')}
                                        </button>
                                        <button
                                            onClick={() => handleAction(onContactClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <MessageCircleIcon className="w-4 h-4" /> {t('contact')}
                                        </button>
                                        <button
                                            onClick={() => handleAction(onTosClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <ClipboardIcon className="w-4 h-4" /> {t('termsOfService')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div>
                                <button 
                                    onClick={() => toggleSection('user')}
                                    className="w-full flex justify-between items-center p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <span className="font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                        {t('myProfile')}
                                        {unreadNotificationCount > 0 && expandedSection !== 'user' && (
                                            <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                                                {notificationsBadge}
                                            </span>
                                        )}
                                    </span>
                                    {expandedSection === 'user' ? <ChevronUpIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
                                </button>
                                {expandedSection === 'user' && (
                                    <div className="bg-slate-50 dark:bg-slate-800/30 py-2">
                                        <button 
                                            onClick={() => handleAction(onPersonalDetailsClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <UserIcon className="w-4 h-4" /> {t('personalDetails')}
                                        </button>
                                        <button 
                                            onClick={() => handleAction(onSettingsClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors"
                                        >
                                            <CogIcon className="w-4 h-4" /> {t('settings')}
                                        </button>
                                        {onNotificationsClick && (
                                            <button
                                                onClick={() => handleAction(onNotificationsClick)}
                                                className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center justify-between gap-3 transition-colors"
                                            >
                                                <span className="flex items-center gap-3 min-w-0">
                                                    <BellIcon className="w-4 h-4 shrink-0" /> {t('notifications')}
                                                </span>
                                                {notificationsBadge && (
                                                    <span className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                                                        {notificationsBadge}
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                        {guided ? (
                                            onConsultationBoxClick && (
                                                <button
                                                    onClick={() => handleAction(onConsultationBoxClick)}
                                                    className="w-full text-start px-8 py-3 text-sm text-emerald-600 dark:text-emerald-400 font-bold hover:text-emerald-800 flex items-center gap-3 transition-colors"
                                                >
                                                    <SparklesIcon className="w-4 h-4" /> {t('consultationBox')}
                                                </button>
                                            )
                                        ) : (
                                        <button 
                                            onClick={() => handleAction(onSuggestionBoxClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-amber-600 dark:text-amber-500 font-bold hover:text-amber-800 dark:hover:text-amber-400 flex items-center gap-3 transition-colors"
                                        >
                                            <SparklesIcon className="w-4 h-4" /> {t('suggestionBox')}
                                        </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {!guided && (
                            <div>
                                <button 
                                    onClick={() => toggleSection('household')}
                                    className="w-full flex justify-between items-center p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <span className="font-medium text-slate-700 dark:text-slate-300">{t('activeHousehold')}</span>
                                    {expandedSection === 'household' ? <ChevronUpIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
                                </button>
                                {expandedSection === 'household' && (
                                    <div className="bg-slate-50 dark:bg-slate-800/30 py-2">
                                        <button 
                                            onClick={() => handleAction(onHouseholdManagementClick)}
                                            className="w-full text-start px-8 py-3 text-sm text-sky-600 dark:text-sky-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 mb-2 transition-colors"
                                        >
                                            <HouseholdIcon className="w-4 h-4" /> {t('manageHouseholds')}
                                        </button>
                                        {switchableHouseholds.map(h => (
                                            <button
                                                key={h.id}
                                                onClick={() => handleAction(() => onSwitchHousehold(h.id))}
                                                className={`w-full text-start px-8 py-3 text-sm transition-colors ${activeHouseholdId === h.id ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-900/20' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}
                                            >
                                                {h.name} {activeHouseholdId === h.id && '✓'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            )}

                            {(canManageUsers || canManageGuidedClients) && (
                                <div>
                                    <button 
                                        onClick={() => toggleSection('admin')}
                                        className="w-full flex justify-between items-center p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                    >
                                        <span className="font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                            {t('admin')}
                                            {isSuperuser && adminAttentionCount > 0 && expandedSection !== 'admin' && (
                                                <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                                                    {adminAttentionCount}
                                                </span>
                                            )}
                                        </span>
                                        {expandedSection === 'admin' ? <ChevronUpIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
                                    </button>
                                    {expandedSection === 'admin' && (
                                        <div className="bg-slate-50 dark:bg-slate-800/30 py-2">
                                            {canManageUsers && (
                                                <button onClick={() => handleAction(onUserManagementClick)} className="w-full text-start px-8 py-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-3 transition-colors">
                                                    <CogIcon className="w-4 h-4" /> {t('manageUsers')}
                                                </button>
                                            )}
                                            {isSuperuser && onSubscriptionManagementClick && (
                                                <button onClick={() => handleAction(onSubscriptionManagementClick)} className="w-full text-start px-8 py-3 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 flex items-center justify-between gap-3 transition-colors">
                                                    <span className="flex items-center gap-3 min-w-0">
                                                        <UsersIcon className="w-4 h-4 shrink-0" /> {t('approveSubs')}
                                                    </span>
                                                    {pendingSubsBadge && (
                                                        <span className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-purple-600 text-white text-[10px] font-black flex items-center justify-center">
                                                            {pendingSubsBadge}
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                            {isSuperuser && onChefManagementClick && (
                                                <button onClick={() => handleAction(onChefManagementClick)} className="w-full text-start px-8 py-3 text-sm text-brand-sage font-bold hover:text-brand-sage/80 flex items-center gap-3 transition-colors">
                                                    <ChefHatIcon className="w-4 h-4" /> {t('manageChefs')}
                                                </button>
                                            )}
                                            {isSuperuser && (
                                                <button onClick={() => handleAction(onSuggestionManagementClick)} className="w-full text-start px-8 py-3 text-sm text-amber-600 dark:text-amber-500 font-bold hover:text-amber-800 dark:hover:text-amber-400 flex items-center justify-between gap-3 transition-colors">
                                                    <span className="flex items-center gap-3 min-w-0">
                                                        <SparklesIcon className="w-4 h-4 shrink-0" /> {t('manageSuggestions')}
                                                    </span>
                                                    {newSuggestionsBadge && (
                                                        <span className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-black flex items-center justify-center">
                                                            {newSuggestionsBadge}
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                            {canManageGuidedClients && onGuidedClientsClick && (
                                                <button
                                                    onClick={() => handleAction(onGuidedClientsClick)}
                                                    className="w-full text-start px-8 py-3 text-sm text-emerald-600 dark:text-emerald-400 font-bold hover:text-emerald-800 dark:hover:text-emerald-300 flex items-center gap-3 transition-colors"
                                                >
                                                    <UsersIcon className="w-4 h-4" /> {t('guidedClientsAdmin')}
                                                </button>
                                            )}
                                            {canManageGuidedClients && onPhraseOfDayAdminClick && (
                                                <button
                                                    onClick={() => handleAction(onPhraseOfDayAdminClick)}
                                                    className="w-full text-start px-8 py-3 text-sm text-brand-sage font-bold hover:text-brand-sage/80 flex items-center gap-3 transition-colors"
                                                >
                                                    <ListBulletIcon className="w-4 h-4" /> {t('phraseOfTheDayAdmin')}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    )}
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 p-6 border-t border-slate-100 dark:border-slate-800 mt-auto pb-8">
                    <button 
                        onClick={() => handleAction(onLogout)}
                        className="w-full flex items-center gap-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors font-medium text-sm"
                    >
                        <LogoutIcon className="w-4 h-4" />
                        <span>{t('logout')}</span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default SideMenu;
